/**
 * Lógica pura de la brújula «Opciones» (barra de pestañas móvil).
 *
 * Cada pantalla declara sus opciones con `useOpcionesPantalla`; la barra las
 * junta y las enseña siempre en el mismo orden, de arriba abajo:
 * «Avisar de un problema aquí» (la primera, arriba del todo, para no mover
 * lo que el pulgar ya conoce) → Filtros → Cambiar de mes/periodo → las
 * acciones propias → «Más» (la
 * última, en gris, en todas las pantallas salvo en la propia página «Más»;
 * internamente es el tipo `ajustes`). «Añadir movimiento» sale en todas,
 * como la última de las propias (justo encima de «Más»). Guía:
 * docs-vadym/navegacion.md.
 */
import type { NombreIcono } from '#components/mobile/ui/Icono';

/** Qué lugar ocupa una opción en la lista (de arriba abajo). */
export type TipoOpcion = 'problema' | 'filtros' | 'mes' | 'propia' | 'ajustes';

export type OpcionPantalla = {
  /** Único dentro de la pantalla (también sirve de `data-testid`). */
  id: string;
  tipo: TipoOpcion;
  texto: string;
  icono: NombreIcono;
  onPress: () => void;
  /** No se enseña (p. ej. «Cubrir» sin categorías en rojo). */
  oculta?: boolean;
  /** Se ve pero no se puede tocar (p. ej. sincronizando). */
  desactivada?: boolean;
  /**
   * Orden dentro de su tipo (menor, más arriba; 0 por defecto). Para que una
   * opción declarada por una parte de la pantalla (p. ej. «Buscar», de la
   * lista) quede detrás de las de la página.
   */
  prioridad?: number;
};

/** Id de «Añadir movimiento»: la brújula la pone en todas las pantallas. */
export const ID_ANADIR_MOVIMIENTO = 'anadir-movimiento';

/**
 * Prioridad reservada de «Añadir movimiento»: siempre la última de las
 * propias, pegada a «Más», para que el pulgar la encuentre sin mirar.
 */
export const PRIORIDAD_ANADIR_MOVIMIENTO = 1_000_000;

/**
 * «Añadir movimiento» con su sitio fijo. La brújula pone una general (sin
 * cuenta ni categoría); una pantalla que sabe la cuenta o la categoría la
 * declara con esta función para que el movimiento nuevo las lleve puestas.
 */
export function opcionAnadirMovimiento(
  texto: string,
  onPress: () => void,
): OpcionPantalla {
  return {
    id: ID_ANADIR_MOVIMIENTO,
    tipo: 'propia',
    texto,
    icono: 'plus',
    onPress,
    prioridad: PRIORIDAD_ANADIR_MOVIMIENTO,
  };
}

/** Id de «Avisar de un problema aquí» (Vadik, 31-oct-2026). */
export const ID_AVISAR_PROBLEMA = 'avisar-problema';

const ORDEN_TIPO: Record<TipoOpcion, number> = {
  problema: -1,
  filtros: 0,
  mes: 1,
  propia: 2,
  ajustes: 3,
};

/**
 * Junta las opciones de todas las partes de la pantalla y las ordena:
 * filtros, mes, propias y «Más» (tipo `ajustes`) al final. Dentro de cada tipo se respeta
 * `prioridad` y luego el orden en que se declararon. Quita las ocultas y los `id` repetidos (se
 * queda con el primero) y deja una sola opción de Ajustes: la de la
 * pantalla si declara una, si no `ajustes` (salvo que venga oculta). Si llega `anadirMovimiento`
 * (la general) se añade salvo que la pantalla declare la suya con el mismo
 * id; sea cual sea, va la última de las propias. `problema` («Avisar de un
 * problema aquí») va la primera de todas.
 */
export function ordenarOpciones(
  opciones: readonly OpcionPantalla[],
  ajustes?: OpcionPantalla,
  anadirMovimiento?: OpcionPantalla,
  problema?: OpcionPantalla,
): OpcionPantalla[] {
  const vistos = new Set<string>();
  const todas = [
    ...(problema ? [{ ...problema, tipo: 'problema' as const }] : []),
    ...opciones,
    ...(anadirMovimiento ? [anadirMovimiento] : []),
  ].map(o =>
    o.id === ID_ANADIR_MOVIMIENTO
      ? { ...o, prioridad: PRIORIDAD_ANADIR_MOVIMIENTO }
      : o,
  );
  const visibles = todas.filter(o => {
    if (o.oculta || vistos.has(o.id)) {
      return false;
    }
    vistos.add(o.id);
    return true;
  });
  const sinAjustes = visibles.filter(o => o.tipo !== 'ajustes');
  const ajustesPropios = visibles.find(o => o.tipo === 'ajustes');
  const ordenadas = sinAjustes
    .map((o, i) => ({ o, i }))
    .sort(
      (a, b) =>
        ORDEN_TIPO[a.o.tipo] - ORDEN_TIPO[b.o.tipo] ||
        (a.o.prioridad ?? 0) - (b.o.prioridad ?? 0) ||
        a.i - b.i,
    )
    .map(({ o }) => o);
  // «Más» oculta (en la propia página «Más») no sale, ni se cambia por otra.
  const ultima = ajustesPropios ?? (ajustes?.oculta ? undefined : ajustes);
  return ultima ? [...ordenadas, ultima] : ordenadas;
}

/** Ruta de la página «Más»: ahí la brújula no ofrece ir a ella misma. */
export function esPaginaMas(ruta: string | undefined): boolean {
  return ruta != null && /^\/mas\/?$/.test(ruta);
}

/** Pestañas de la barra (la brújula no es una pestaña: abre las opciones). */
export type Pestana = 'plan' | 'cuentas' | 'inicio' | 'informes' | 'gastos';

const RUTAS_PESTANA: [Pestana, RegExp][] = [
  ['inicio', /^\/(inicio|revisar)(\/|$)/],
  ['plan', /^\/(budget|asignar|anual|ahorros|categories)(\/|$)/],
  ['informes', /^\/reports(\/|$)/],
  ['cuentas', /^\/accounts(\/|$)/],
  ['gastos', /^\/(gastos|transactions)(\/|$)/],
];

/**
 * Pestaña que se ilumina en cada ruta: las pantallas de detalle cuentan como
 * la pestaña de la que cuelgan (una cuenta → Cuentas, una categoría → Plan).
 * Las páginas generales (Ajustes, Más, Reglas…) no iluminan ninguna.
 */
export function pestanaActiva(ruta: string): Pestana | null {
  return RUTAS_PESTANA.find(([, re]) => re.test(ruta))?.[0] ?? null;
}

// Formularios a pantalla completa con su propio pie (Guardar, Borrar…): ahí
// la barra estorbaría y no tienen menús que llevar a la brújula.
const SIN_BARRA = [
  /^\/transactions\/[^/]+/,
  /^\/rules\/[^/]+/,
  /^\/payees\/[^/]+/,
  /^\/bank-sync\/account\//,
  /^\/accounts\/[^/]+\/importar$/,
  /^\/compartido$/,
  /^\/(tags|user-directory|user-access|enablebanking)(\/|$)/,
];

/** ¿Se pinta la barra de pestañas (y con ella la brújula) en esta ruta? */
export function barraVisibleEn(ruta: string): boolean {
  return !SIN_BARRA.some(re => re.test(ruta));
}
